import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * The campaign address book — people who are neither on our voter register nor our staff.
 *
 * ## There is no field here carrying a phone NUMBER
 *
 * Only `phoneMask`. That is the disclosure decision, not an oversight: a list of four
 * hundred names on a screen must not also be a way to take four hundred numbers off it.
 */

export interface Contact {
  id: string
  fullName: string
  kindCode: string
  kindLabel: string
  title: string | null
  organisation: string | null
  /** `+2547••••1234` — enough to recognise, not enough to dial. */
  phoneMask: string | null
  hasPhone: boolean
  email: string | null
  unitId: string | null
  unitName: string | null
  unitPath: string | null
  ownerUserId: string | null
  ownerName: string | null
  ownerMobilizerId: string | null
  dispositionCode: string | null
  notes: string | null
  lastContactedOn: string | null
  archivedAt: string | null
  archiveReason: string | null
  createdAt: string
}

export interface Vocab {
  code: string
  label: string
}

interface Paged<T> {
  content: T[]
  totalElements: number
  number: number
  size: number
}

export const listContacts = (p: {
  q?: string; kind?: string; unitId?: string; includeArchived?: boolean
  page?: number; size?: number
} = {}) => api.get<Paged<Contact>>(`/contacts${qs(p)}`)

export const getContact = (id: string) => api.get<Contact>(`/contacts/${id}`)

export interface SaveContact {
  fullName: string
  kindCode: string
  title?: string | null
  organisation?: string | null
  phoneCountry?: string | null
  phone?: string | null
  email?: string | null
  unitId?: string | null
  ownerUserId?: string | null
  ownerMobilizerId?: string | null
  dispositionCode?: string | null
  notes?: string | null
  lastContactedOn?: string | null
}

export const saveContact = (body: SaveContact, id?: string) => id
  ? api.put<Contact>(`/contacts/${id}`, body)
  : api.post<Contact>('/contacts', body)

/** Archive, never delete — a contact who moved on is still the record of who introduced us. */
export const archiveContact = (id: string, reason?: string | null) =>
  api.post<Contact>(`/contacts/${id}/archive`, { reason: reason ?? null })

export const restoreContact = (id: string) =>
  api.post<Contact>(`/contacts/${id}/restore`, {})

export const contactKinds = () => api.get<Vocab[]>('/contacts/vocabulary/kinds')
export const contactDispositions = () => api.get<Vocab[]>('/contacts/vocabulary/dispositions')
